import { useCallback } from "react";
import { DetailsApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";
import { DetailsResponse, Stage } from "../lusca-search-api/generated-src";
import removeEmpty from "../helpers/removeEmpty";
import { decompressAndDeserializeData } from "../helpers/compressionUtils";
import {
  AccountingEventsGroup,
  CanonicalBusinessEvent,
  DigitalBusinessDocument,
} from "../types";
import { isDigitalDocument, isDocumentFlowCbe } from "../helpers/typeChecks";
import { FLOW_TYPES } from "../helpers/flowTypeUtils";

const processRawData = (data: DetailsResponse, currentFlowType: string) => {
  const events = (data.events || []).map((event) => {
    const cleanEvent = removeEmpty(event);

    if (cleanEvent.body && typeof cleanEvent.body === "string") {
      cleanEvent.body = decompressAndDeserializeData(cleanEvent.body);
    }

    return "documentType" in cleanEvent
      ? (cleanEvent as DigitalBusinessDocument)
      : (cleanEvent as AccountingEventsGroup);
  });

  const cbes = (data.cbes || []).map((cbe) => {
    const cleanCbe = removeEmpty(cbe);
    if (
      cleanCbe.businessEvents &&
      typeof cleanCbe.businessEvents === "string"
    ) {
      cleanCbe.businessEvents = decompressAndDeserializeData(
        cleanCbe.businessEvents
      );
    }
    return cleanCbe as CanonicalBusinessEvent;
  });

  const rawTotals = {
    totalEvents: events.length,
    totalCbes: cbes.length,
  };

  // Filter results according to current flow type
  const filteredEvents =
    currentFlowType === FLOW_TYPES.DOCUMENT
      ? events.filter((event) => isDigitalDocument(event))
      : events.filter((event) => !isDigitalDocument(event));

  const filteredCbes =
    currentFlowType === FLOW_TYPES.DOCUMENT
      ? cbes.filter((cbe) => isDocumentFlowCbe(cbe))
      : cbes.filter((cbe) => !isDocumentFlowCbe(cbe));

  return {
    events: filteredEvents,
    cbes: filteredCbes,
    rawTotals,
    rawCbes: cbes,
  };
};

export default function useFetchCrossStageDetails() {
  return useCallback(
    async (
      targetStage: Stage,
      identifier: string,
      currentFlowType: string
    ) => {
      try {
        const api = DetailsApiFactory();
        const response = await api.getDetails(targetStage, identifier);
        return processRawData(response.data, currentFlowType);
      } catch (error) {
        console.error("Failed to fetch cross-stage data:", error);
        throw error;
      }
    },
    []
  );
}
